import type { User } from 'firebase/auth';
import { useNavigate } from 'react-router';
import type { CustomerInfo } from '@revenuecat/purchases-js';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '@/contexts/useCurrentUser';
import { subscriptionService } from '@/services/subscriptionService';
import type { UserProfile } from '@/types';
import { useAuth } from './useAuth';
import { useFeatureFlag } from './useFeatureFlag';

function useRevenueCatCustomerInfo(user: User | null, enabled: boolean) {
  return useQuery<CustomerInfo | null>({
    queryKey: ['customerInfo', user?.uid],
    queryFn: async () => {
      if (!user || !enabled) return null;
      await subscriptionService.configure(user.uid, {
        email: user.email,
        displayName: user.displayName,
      });
      return subscriptionService.getCustomerInfo();
    },
    enabled: !!user && enabled,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: 'always',
  });
}

interface SubscriptionState {
  hasSubscription: boolean;
  isCancelled: boolean;
  isPending: boolean;
  isOutOfSync: boolean;
  activeUntilDate: Date | null;
  managementURL: string | null;
  provider: 'stripe' | 'revenuecat' | null;
}

function deriveSubscriptionState(
  currentUser: UserProfile | null | undefined,
  customerInfo: CustomerInfo | null | undefined
): SubscriptionState {
  const provider = currentUser?.subscription?.provider;
  const isStripe = provider === 'stripe';

  const rcActive = customerInfo
    ? Object.keys(customerInfo.entitlements?.active ?? {}).length > 0
    : null;

  const resolvedProvider = provider ?? (rcActive ? 'revenuecat' : null);
  const firestoreActive = currentUser?.subscription?.active ?? false;
  const hasSubscription = firestoreActive || rcActive || false;

  const isOutOfSync =
    resolvedProvider === 'revenuecat' && rcActive !== null && rcActive !== firestoreActive;

  return {
    hasSubscription,
    isCancelled: currentUser?.subscription?.cancelled ?? false,
    isPending: currentUser?.subscription?.pending ?? false,
    isOutOfSync,
    activeUntilDate: currentUser?.subscription?.expiresAt
      ? new Date(currentUser.subscription.expiresAt)
      : null,
    managementURL: isStripe ? null : (customerInfo?.managementURL ?? null),
    provider: resolvedProvider,
  };
}

export function useSubscription() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentUser } = useCurrentUser();
  const { enabled: stripeEnabled, isLoading: flagLoading } = useFeatureFlag('stripe_payments');

  const provider = currentUser?.subscription?.provider;
  const isStripe = provider === 'stripe';
  const isRevenueCat = provider === 'revenuecat';
  const shouldFetchRevenueCat = !isStripe && (!stripeEnabled || isRevenueCat);

  const {
    data: customerInfo,
    isLoading: rcLoading,
    refetch: refetchRc,
  } = useRevenueCatCustomerInfo(user, shouldFetchRevenueCat);

  const state = deriveSubscriptionState(currentUser, customerInfo);

  async function sync(): Promise<{ active: boolean }> {
    const result = await subscriptionService.syncSubscription();
    await refetchRc();
    return { active: result.active };
  }

  async function restore(): Promise<{ active: boolean }> {
    const { active } = await sync();
    if (active) {
      navigate('/discover');
    }
    return { active };
  }

  async function presentPaywall(): Promise<{ active: boolean }> {
    if (stripeEnabled) {
      navigate('/account/subscription/paywall');
      return { active: false };
    }

    const newCustomerInfo = await subscriptionService.presentPaywall();
    const status = await subscriptionService.syncSubscription();
    await refetchRc();

    const active =
      Object.keys(newCustomerInfo.entitlements?.active ?? {}).length > 0 || status.active;

    return { active };
  }

  async function manage(): Promise<void> {
    if (state.managementURL) {
      window.location.href = state.managementURL;
      return;
    }

    if (state.provider === 'stripe') {
      const result = await subscriptionService.createStripeCustomerPortalSession(
        window.location.origin
      );
      window.location.href = result.url;
      return;
    }

    throw new Error('No management URL available');
  }

  return {
    ...state,
    isLoading: rcLoading || flagLoading,
    refetch: refetchRc,
    presentPaywall,
    sync,
    restore,
    manage,
  };
}
